import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toastSuccess } from '../utils/toasts';
import { useApproveRequestMutation, useCreateApprovedWireRequestMutation, useGetBankAccountsQuery } from '../store';
import useGetParams from '../hooks/useGetParams';
import { ApproveWireTransferDTO, CreateApprovedWireRequestDTO, WireRequestDTO } from '../models/wire';
import Input from './atoms/Input';
import Button from './atoms/Button';
import FileUpload from './FileUpload';
import { BaseData } from './Table/Table';
import CurrencyInput from './atoms/CurrencyInput';
import Select from './atoms/Select/Select';

interface BankAccountsWire extends BaseData {
  status: JSX.Element;
  requested_at: JSX.Element;
  transferred_at: JSX.Element | string;
  requested_by: JSX.Element | string;
  transferred_by: JSX.Element | string;
  amount: JSX.Element | string;
  description: JSX.Element | string;
}

interface ApproveWireRequestProps {
  data: WireRequestDTO[];
  row: BankAccountsWire;
  setShowModal: (showModal: boolean) => void;
  view?: boolean;
  superadmin?: boolean;
  root?: boolean;
}

const ApproveWireRequest = ({
  data, row, setShowModal, view, superadmin, root,
}: ApproveWireRequestProps) => {
  const [approveRequest, resApproveRequest] = useApproveRequestMutation();
  const [createApprovedWireRequest, resCreateApprovedWireRequest] = useCreateApprovedWireRequestMutation();
  const { eventId, businessId } = superadmin ? { eventId: '', businessId: '' } : useGetParams();
  const { data: bankAccounts } = useGetBankAccountsQuery({ businessId });
  const {
    register, handleSubmit, setValue, formState: { errors }, watch,
  } = useForm<ApproveWireTransferDTO | CreateApprovedWireRequestDTO>({
    resolver: yupResolver(yup.object().shape({
      description: yup.string().required('La observación es requerida'),
      receipt_pdf_url: yup.string().required('El comprobante es requerido'),
      ...(root && {
        bank_account_id: yup.string().required('La cuenta es requerida'),
        amount: yup.number().required('El monto es requerido'),
      }),
    })),
    defaultValues: {
      description: view ? row.description as string : '',
      receipt_pdf_url: '',
      ...(root && {
        bank_account_id: '',
        amount: 0,
      }),
    },
  });

  useEffect(() => {
    if (resApproveRequest.isSuccess) {
      toastSuccess('Solicitud aprobada correctamente');
      setShowModal(false);
    }
  }, [resApproveRequest.isSuccess, setShowModal]);

  useEffect(() => {
    if (resCreateApprovedWireRequest.isSuccess) {
      setShowModal(false);
      toastSuccess('Solicitud de dinero aprobada');
    }
  }, [resCreateApprovedWireRequest.isSuccess, setShowModal]);

  useEffect(() => {
    if (view) {
      setValue('description', row.description as string);
      setValue('receipt_pdf_url', data.find((wire) => wire.id === row.id)?.wire_transfer?.receipt_pdf_url || '');
    }
  }, [view, row, data, setValue]);

  const onSubmit = (formData: ApproveWireTransferDTO | CreateApprovedWireRequestDTO) => {
    if (root) {
      createApprovedWireRequest({
        eventId,
        businessId: superadmin ? undefined : businessId,
        wireData: formData as CreateApprovedWireRequestDTO,
      });
    } else {
      approveRequest({
        eventId: superadmin ? String(row.event_id) : eventId,
        wireId: row.id,
        wireData: formData as ApproveWireTransferDTO,
      });
    }
  };

  const dropDownData = bankAccounts && bankAccounts.length > 0 ? bankAccounts?.map((item) => ({
    label: `${item.holder_name} - ${item.entity_name}`,
    value: item.id,
  })) : [{ label: 'No hay cuentas disponibles', value: '' }];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='flex flex-col gap-4 mt-4'>
        <Input
          id='requested_by'
          label='Solicitado por *'
          type='text'
          labelAbove
          value={data.find((wire) => wire.id === row.id)?.request_user?.username}
          disabled
        />
        {!root && (
          <Input
            id='CBU/CVU'
            label='CBU/CVU *'
            type='text'
            labelAbove
            value={data.find((wire) => wire.id === row.id)?.bank_account?.transfer_code}
            disabled
          />
        )}
        {root && (
          <div className='w-full'>
            <Select
              id='bank_account_id'
              label='Cuenta *'
              options={dropDownData || []}
              value={watch('bank_account_id')}
              onChange={(e) => setValue('bank_account_id', e.target.value)}
            />
          </div>
        )}
        <CurrencyInput
          id='amount'
          label='Monto a transferir *'
          labelAbove
          value={root ? watch('amount') : data.find((wire) => wire.id === row.id)?.amount || 0}
          onChange={(value) => setValue('amount', value)}
          disabled={!root}
        />
        <Input
          id='description'
          label='Observación'
          type='text'
          labelAbove
          {...register('description')}
          disabled={view}
          errMsg={errors.description?.message}
        />
        <div>
          <FileUpload
            accept={['application/pdf', 'image/png', 'image/jpeg']}
            onFileUploaded={(url) => {
              console.log('File uploaded:', url);
              setValue('receipt_pdf_url', url);
            }}
            defaultFileUrl={data.find((wire) => wire.id === row.id)?.wire_transfer?.receipt_pdf_url}
          />
          <p className='text-sm text-red-500'>{errors.receipt_pdf_url?.message}</p>
        </div>
      </div>
      <div className='flex flex-row gap-2 mt-4'>
        <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
        <Button
          variant='primary'
          type={view ? 'button' : 'submit'}
          onClick={() => {
            if (view) {
              setShowModal(false);
            }
          }}
          loading={resApproveRequest.isLoading || resCreateApprovedWireRequest.isLoading}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {view ? 'Aceptar' : root ? 'Crear' : 'Aprobar'}
        </Button>
      </div>
    </form>
  );
};

export default ApproveWireRequest;
